import {
  LegacySearchHit,
  ModernSearchHit,
  SearchHit,
  SearchQueryIndex,
  SearchDisplay,
  PageProps,
} from "frontend/types";
import {
  createLegacySearchArticleTease,
  createModernSearchArticleTease,
  isRisFacultyProfileHit,
  normalizeFacultyProfileLink,
} from "./search-article-tease";
import { LazyTeaseFeedTease } from "design-system/components/blocks/tease-feed/tease-feed-tease";
import { LazyTeaseRowTease } from "design-system/components/blocks/tease-row/tease-row-tease";
import { createModernSearchProgramTease } from "./search-program-tease";
import {
  createModernSearchBookTease,
  createSearchBookTease,
} from "./search-book-tease";
import { createModernSearchVideoTease } from "./search-video-tease";
import { isSearchHitType } from "./is-search-hit-type";
import { createModernSearchPersonTease } from "./search-person-tease";
import { createModernSearchParticipantStoryTease } from "./search-participant-story";
import {
  createLegacyEventTeaseProps,
  createEventFeedTeaseProps,
} from "./search-event-tease";
import { createModernSearchResultPersonTease } from "./search-result-person-tease";
export function createSearchTease(
  hit: SearchHit,
  index: SearchQueryIndex,
  teaseType?:
    | Exclude<LazyTeaseFeedTease["props"], undefined>["type"]
    | "Best Bet"
    | "Book"
    | "Book Component"
    | "Citation"
    | "Club Page"
    | "Course Materials"
    | "Database"
    | "Dataset"
    | "Detail"
    | "Directory"
    | "Association Reference"
    | "Featured Work"
    | "HBS Case Material"
    | "HBS Teaching and Training Material"
    | "Honors and Awards"
    | "Interactive Media"
    | "IssueLanding"
    | "knowledgeBaseArticlePage"
    | "Landing"
    | "Non-HBS Case Material"
    | "Non-HBS Teaching and Training Material"
    | "Other"
    | "Participant Story"
    | "Podcast"
    | "Presentation"
    | "Profile"
    | "Published Article"
    | "Research"
    | "Research Summary"
    | "Teaching Interest"
    | "Teaching Interests Summary"
    | "Teaching Materials"
    | "Unpublished Work"
    | "Unpublished Works"
    | "Video Citation"
    | "Working Paper",
  siteId?: string,
  template?: string,
  options?: {
    pageProps?: PageProps;
    featureFacultyInArticles?: boolean;
    portraitLimit?: number;
  },
): LazyTeaseFeedTease | LazyTeaseRowTease | undefined {
  if (
    (index === "modern" ||
      index === "knowledge" ||
      index === "classnotes" ||
      index === "protected") &&
    (isSearchHitType<SearchDisplay>("modern", hit) ||
      isSearchHitType<ModernSearchHit>("modern", hit))
  ) {
    if (template === "Person Profiles" && isRisFacultyProfileHit(hit)) {
      const props = createModernSearchPersonTease(hit);
      if (!props) return undefined;

      props.link = normalizeFacultyProfileLink(hit.url);
      props.shortBio = hit.display?.jobTitle?.replace(
        /\s*\.?\s*<br\s*\/?>\s*/gi,
        ". ",
      );
      props.titles = undefined;
      return { name: "PersonTease", props };
    }

    teaseType = teaseType || hit.contentType || "Article";
    if (
      teaseType === "Article" ||
      hit.contentType === "Best Bet" ||
      hit.contentType === "Participant Story" ||
      hit.contentType === "knowledgeBaseArticlePage" ||
      hit.contentType === "Detail" ||
      hit.contentType === "Landing" ||
      hit.contentType === "Database" ||
      hit.contentType === "Dataset" ||
      hit.contentType === "Citation" ||
      hit.contentType === "Association Reference" ||
      hit.contentType === "Podcast" ||
      hit.contentType === "Club Page" ||
      (hit.contentType === "IssueLanding" && !template) ||
      (teaseType === "Profile" && !template)
    ) {
      return {
        name: "ArticleTease",
        props: createModernSearchArticleTease(hit, siteId, { ...options }),
      };
    }

    // RIS Publications - render with citation as tease text
    if (
      teaseType === "Book" ||
      teaseType === "Book Component" ||
      teaseType === "Course Materials" ||
      teaseType === "Featured Work" ||
      teaseType === "HBS Case Material" ||
      teaseType === "HBS Teaching and Training Material" ||
      teaseType === "Honors and Awards" ||
      teaseType === "Interactive Media" ||
      teaseType === "Non-HBS Case Material" ||
      teaseType === "Non-HBS Teaching and Training Material" ||
      teaseType === "Other" ||
      teaseType === "Presentation" ||
      teaseType === "Published Article" ||
      teaseType === "Research" ||
      teaseType === "Research Summary" ||
      teaseType === "Teaching Interest" ||
      teaseType === "Teaching Interests Summary" ||
      teaseType === "Teaching Materials" ||
      teaseType === "Unpublished Work" ||
      teaseType === "Working Paper"
    ) {
      return {
        name: "ArticleTease",
        props: createModernSearchArticleTease(hit, siteId, { ...options }),
      };
    }

    if (teaseType === "Program") {
      return {
        name: "ProgramTease",
        props: createModernSearchProgramTease(hit),
      };
    }

    if (teaseType === "Course") {
      return {
        name: "ProgramTease",
        props: createModernSearchProgramTease(hit),
      };
    }

    if (hit.contentType === "Video Citation") {
      return {
        name: "VideoTease",
        props: createModernSearchVideoTease(hit),
      };
    }

    if (teaseType === "Profile") {
      const teaseItem = createModernSearchPersonTease(hit);
      return {
        name: "PersonTease",
        props: teaseItem,
      };
    }

    if (teaseType === "Participant Story") {
      const teaseItem = createModernSearchParticipantStoryTease(hit);
      return {
        name: "ParticipantStoryTease",
        props: teaseItem,
      };
    }
    if (teaseType === "Event" || hit.contentType === "Event") {
      const teaseItem = createEventFeedTeaseProps(hit as SearchDisplay);
      return {
        name: "EventFeedTease",
        props: teaseItem,
      };
    }
    if (template === "Alumni Magazine") {
      const teaseItem = createModernSearchBookTease(hit as SearchDisplay);
      return {
        name: "BookTease",
        props: teaseItem,
      };
    }
  }

  if (index === "legacy" && isSearchHitType<LegacySearchHit>("legacy", hit)) {
    teaseType = teaseType || "Article";
    if (teaseType === "Article") {
      return {
        name: "ArticleTease",
        props: createLegacySearchArticleTease(hit),
      };
    }
    if (teaseType === "Book") {
      return { name: "BookTease", props: createSearchBookTease(hit) };
    }
  }

  //protected index
  if (
    index === "protected" &&
    isSearchHitType<LegacySearchHit>("legacy", hit)
  ) {
    teaseType = teaseType || "Article";
    if (teaseType === "Article") {
      return {
        name: "ArticleTease",
        props: createLegacySearchArticleTease(hit),
      };
    }
    if (teaseType === "Event") {
      return {
        name: "EventFeedTease",
        props: createLegacyEventTeaseProps(hit),
      };
    }
  }

  if (
    index === "alumniDirectory" &&
    isSearchHitType<ModernSearchHit>("modern", hit)
  ) {
    const teaseItem = createModernSearchResultPersonTease(hit);
    return {
      name: "PersonSearchResultTease",
      props: teaseItem,
    };
  }

  console.warn(
    "createSearchTease > search tease not yet implemented:",
    hit,
    index,
    teaseType,
  );
}
